import { create } from 'zustand';

/**
 * Recently-accessed customers — the last 5 customers this staff member opened
 * (detail panel or device drill-in), shown in the Users search column's empty
 * state so you can jump straight back without re-searching.
 *
 * Stored client-side in localStorage (per-browser) — a deliberate v1 choice for
 * simplicity. It does NOT follow you to another device/browser. The scio3
 * equivalent is server-backed (a `saveRecentData` mutation + `*Recents` query);
 * if cross-device recents are ever needed, upgrade this to a staff-api
 * `staff_recent_customers` collection + ops.
 */
export interface RecentCustomer {
  accountId: string;
  companyName?: string;
  ownerEmail?: string;
  accessedAt: number;
}

const STORAGE_KEY = 'scio3-staff-recent-customers';
const MAX_RECENTS = 5;

function loadRecents(): RecentCustomer[] {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((r): r is RecentCustomer => !!r && typeof r.accountId === 'string')
      .slice(0, MAX_RECENTS);
  } catch {
    return [];
  }
}

function persist(recents: RecentCustomer[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recents));
  } catch {
    // ignore quota / private-mode errors
  }
}

interface RecentCustomersState {
  recents: RecentCustomer[];
  addRecent: (c: { accountId?: string | null; companyName?: string; ownerEmail?: string }) => void;
}

export const useRecentCustomersStore = create<RecentCustomersState>((set, get) => ({
  recents: loadRecents(),

  addRecent: ({ accountId, companyName, ownerEmail }) => {
    if (!accountId) return;
    const current = get().recents;
    // De-dupe by accountId, newest first, cap at MAX_RECENTS.
    const next: RecentCustomer[] = [
      { accountId, companyName, ownerEmail, accessedAt: Date.now() },
      ...current.filter((r) => r.accountId !== accountId),
    ].slice(0, MAX_RECENTS);
    persist(next);
    set({ recents: next });
  },
}));
